/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-15 14:21:40
 *
 *******************************************************/

import _ from 'lodash';
import React, { Component } from 'react';
import Classnames from 'classnames';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Stack, Modal, Tabs, Card, ButtonGroup, Button, Badge } from '@shopify/polaris';
import ProductItemTab from './product-item-tab';
import CollectionHeaderTab from './collection-header-tab';
import PageTemplates from 'src/components/containers/theme-settings/page-templates';
import PreviewTemplate from 'src/components/views/theme-settings/preview/page-templates/preview-template';
import { MENU_ID } from 'src/constants/theme-settings.jsx';
import { MAIN_LAYOUT_VALUE } from 'src/constants/theme-settings.js';
import { ReactComponent as AppMajor } from 'src/assets/images/theme-setting/app-major.svg';

type Props = {
  formValues: any,
  updateField: (field: string, value: any) => void,
  show: boolean,
  onClose: Function,
  onConfirm: Function,
  onSelectTemplate: Function,
  mainLayout: string,
  subLayout: string,
  collectionHeader: string,
  firstTimeActivate: boolean,
  boostThemeLib: string,
  menuLayoutModalId: number,
  updateFieldReducer: (fieldName: string, data: any) => void
};

type ComposedProps = Props & WithI18nProps;

class LayoutModal extends Component<ComposedProps> {
  state = {
    selectedTab: 0,
    previewTemplate: null
  };

  tabs = [
    {
      id: 'product-item',
      content: this.props.i18n.translate('AdminHTML.ThemeSetting.LayoutModal.changeLayout_Tab1'),
      panelID: 'product-item',
      component: ProductItemTab,
      prefix: MENU_ID.PRODUCT_ITEM
    },
    {
      id: 'collection-header',
      content: this.props.i18n.translate('AdminHTML.ThemeSetting.LayoutModal.changeLayout_Tab2'),
      panelID: 'collection-header',
      component: CollectionHeaderTab,
      prefix: MENU_ID.COLLECTION_HEADER
    }
  ];

  menuTabs = [
    {
      id: 0,
      title: this.props.i18n.translate('AdminHTML.ThemeSetting.LayoutModal.menuItem_1')
    },
    {
      id: 1,
      title: this.props.i18n.translate('AdminHTML.ThemeSetting.LayoutModal.menuItem_2'),
      isBeta: true
    }
  ];

  componentWillReceiveProps(nextProps) {
    const { isOpenConfirmChangeTemplateModal } = this.props;
    const {
      isOpenConfirmChangeTemplateModal: nextIsOpenConfirmChangeTemplateModal,
      updateFieldReducer
    } = nextProps;

    if (isOpenConfirmChangeTemplateModal && !nextIsOpenConfirmChangeTemplateModal) {
      this.setState({
        selectedTab: 0,
        previewTemplate: null
      });
      updateFieldReducer('menuLayoutModalId', 0);
    }
  }

  handleTabChange = tabIndex => {
    this.setState({
      selectedTab: tabIndex
    });
  };

  handleMenuTabChange = tabIndex => {
    const { updateFieldReducer } = this.props;
    updateFieldReducer('menuLayoutModalId', tabIndex);
  };

  handleSelectTemplate = theme => {
    this.props.onSelectTemplate(theme);
  };

  handleViewDemoTemplate = theme => {
    this.setState({
      previewTemplate: theme
    });
  };

  handleCloseTemplate = () => {
    const { onClose, updateFieldReducer } = this.props;
    onClose();
    this.setState({
      selectedTab: 0,
      previewTemplate: null
    });
    updateFieldReducer('menuLayoutModalId', 0);
  };

  isEnable = () => {
    const {
      mainLayout: mainLayoutReducer,
      subLayout: subLayoutReducer,
      collectionHeader: collectionHeaderReducer
    } = this.props;

    // If change to Match theme main layout -> enable
    if (mainLayoutReducer === MAIN_LAYOUT_VALUE.MATCH_THEME) {
      return true;
    }

    if (mainLayoutReducer !== MAIN_LAYOUT_VALUE.MATCH_THEME && subLayoutReducer) {
      return true;
    }

    if (collectionHeaderReducer) {
      return true;
    }

    return false;
  };

  renderContent = () => {
    const {
      formValues,
      updateField,
      mainLayout,
      subLayout,
      collectionHeader,
      boostThemeLib,
      updateFieldReducer,
      onChangeFilterLayout,
      menuLayoutModalId
    } = this.props;
    const { selectedTab } = this.state;

    const TabContent = this.tabs[selectedTab].component;

    if (menuLayoutModalId === 0) {
      return (
        <Tabs tabs={this.tabs} selected={selectedTab} onSelect={this.handleTabChange}>
          <Card.Section>
            <TabContent
              prefix={this.tabs[selectedTab].prefix}
              formValues={formValues}
              updateField={updateField}
              mainLayout={mainLayout}
              subLayout={subLayout}
              collectionHeader={collectionHeader}
              updateFieldReducer={updateFieldReducer}
              boostThemeLib={boostThemeLib}
              onChangeFilterLayout={onChangeFilterLayout}
            />
          </Card.Section>
        </Tabs>
      );
    }

    return (
      <PageTemplates
        onSelectTemplate={this.handleSelectTemplate}
        onViewDemoTemplate={this.handleViewDemoTemplate}
      />
    );
  };

  render() {
    const { i18n, show, firstTimeActivate, onConfirm, menuLayoutModalId } = this.props;
    const { previewTemplate } = this.state;

    return (
      <Modal
        large
        fullScreen
        open={show}
        onClose={this.handleCloseTemplate}
        instant
        noScroll
        title={
          <Stack alignment="center" spacing="tight">
            <Stack.Item>
              <div className="BC-theme-setting-layout-modal-icon-title">
                <AppMajor />
              </div>
            </Stack.Item>
            <Stack.Item>
              <p>{i18n.translate('AdminHTML.ThemeSetting.Common.layoutLibrary')}</p>
            </Stack.Item>
          </Stack>
        }
        footer={
          menuLayoutModalId === 0 ? (
            <Stack alignment="center">
              <Stack.Item fill />
              <Stack.Item>
                <div className="btn-group-polaris">
                  <ButtonGroup>
                    <Button onClick={this.handleCloseTemplate}>
                      {i18n.translate('AdminHTML.Common.Action.cancel')}
                    </Button>
                    <Button onClick={onConfirm} primary disabled={!this.isEnable()}>
                      {firstTimeActivate
                        ? i18n.translate('AdminHTML.ThemeSetting.LayoutModal.useThisLayout_Action')
                        : i18n.translate('AdminHTML.ThemeSetting.LayoutModal.changeLayout_Action')}
                    </Button>
                  </ButtonGroup>
                </div>
              </Stack.Item>
            </Stack>
          ) : null
        }>
        <Modal.Section>
          <div className="BC-theme-setting-layout-modal">
            {!_.isEmpty(previewTemplate) ? (
              <PreviewTemplate
                theme={previewTemplate}
                onSelectTemplate={this.handleSelectTemplate}
                onBack={() =>
                  this.setState({
                    previewTemplate: ''
                  })
                }
              />
            ) : (
              <>
                <div className="BC-theme-setting-layout-modal__menu">
                  {this.menuTabs.map(item => (
                    <div
                      className={Classnames('BC-theme-setting-layout-modal__menu-item', {
                        active: item.id === menuLayoutModalId
                      })}
                      onClick={() => this.handleMenuTabChange(item.id)}
                      key={item.id}>
                      <span>{item.title}</span>
                      {item.isBeta && (
                        <Badge status="info">{i18n.translate('AdminHTML.Common.Badge.beta')}</Badge>
                      )}
                    </div>
                  ))}
                </div>

                <div className="BC-theme-setting-layout-modal__content">{this.renderContent()}</div>
              </>
            )}
          </div>
        </Modal.Section>
      </Modal>
    );
  }
}

export default withI18n()(LayoutModal);
